// Local-only unsupported-format classification. This module returns one coarse,
// closed label and never exposes a filename, MIME string, clipboard content, or
// parser error to analytics.
const EXTENSIONS = new Map([
  ['parquet', 'parquet'],
  ['xls', 'xlsx'],
  ['xlsx', 'xlsx'],
  ['xlsm', 'xlsx'],
  ['ods', 'xlsx'],
  ['xml', 'xml'],
  ['xsd', 'xml'],
  ['xsl', 'xml'],
  ['pdf', 'pdf'],
  ['doc', 'docx'],
  ['docx', 'docx'],
  ['odt', 'docx'],
  ['rtf', 'docx'],
  ['png', 'image'],
  ['jpg', 'image'],
  ['jpeg', 'image'],
  ['gif', 'image'],
  ['webp', 'image'],
  ['avif', 'image'],
  ['bmp', 'image'],
  ['heic', 'image'],
  ['svg', 'image'],
  ['zip', 'binary'],
  ['gz', 'binary'],
  ['7z', 'binary'],
  ['rar', 'binary'],
  ['wasm', 'binary'],
  ['exe', 'binary'],
  ['dmg', 'binary'],
]);

/** Classify an uploaded/dropped File without returning any identifying data. */
export function unsupportedFormatForFile(file) {
  if (!file) return null;
  const name = String(file.name || '');
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : '';
  if (EXTENSIONS.has(ext)) return EXTENSIONS.get(ext);

  const type = String(file.type || '').toLowerCase();
  if (type.startsWith('image/')) return 'image';
  if (type === 'application/pdf') return 'pdf';
  if (/spreadsheet|excel|opendocument\.spreadsheet/.test(type)) return 'xlsx';
  if (/wordprocessing|msword|opendocument\.text|rtf/.test(type)) return 'docx';
  if (/xml/.test(type)) return 'xml';
  if (/parquet/.test(type)) return 'parquet';
  if (type === 'application/octet-stream' || /zip|compressed|x-7z|rar/.test(type)) return 'binary';
  if (type && !type.startsWith('text/') && !/json|ya?ml|csv|javascript|typescript/.test(type)) return 'other';
  return null;
}

/** Classify pasted text by a short local signature only. */
export function unsupportedFormatForText(text) {
  const sample = String(text || '').slice(0, 1024);
  const head = sample.trimStart();
  if (!head) return null;
  if (head.startsWith('PAR1')) return 'parquet';
  if (head.startsWith('%PDF-')) return 'pdf';
  if (head.startsWith('\u0089PNG') || head.startsWith('GIF87a') || head.startsWith('GIF89a') || head.startsWith('\u00ff\u00d8\u00ff')) return 'image';
  if (head.startsWith('PK\u0003\u0004')) return 'binary';
  // HTML is ordinary text/code input for cruxdiff. Its doctype and root element
  // are strong enough to distinguish it from unsupported XML without guessing
  // about inherently ambiguous fragments such as a lone custom element.
  if (/^(?:<!doctype\s+html\b|<html\b)/i.test(head)) return null;
  if (/^(?:<\?xml\b|<[A-Za-z_][\w:.-]*(?:\s|\/?\s*>))/i.test(head)) return 'xml';

  const controls = [...sample].filter((ch) => {
    const n = ch.charCodeAt(0);
    return n === 0 || (n < 32 && ch !== '\n' && ch !== '\r' && ch !== '\t');
  }).length;
  return controls > Math.max(1, sample.length * 0.01) ? 'binary' : null;
}

/** Clipboard adapter kept separate so the DOM wiring never handles raw values. */
export function unsupportedFormatForPaste(clipboard) {
  const file = clipboard?.files?.[0];
  return unsupportedFormatForFile(file) || unsupportedFormatForText(clipboard?.getData?.('text/plain') || '');
}
